/**
 * HMAC Signature Validation Utility
 * 
 * Purpose: Client-side validation of quota API signatures to prevent tampering
 * Security: Only validates timestamp expiry - HMAC verification stays on backend
 * 
 * @module signatureValidator
 */

/**
 * Validates if a quota signature is still valid
 * 
 * @param {Object} quotaData - Quota response from API
 * @param {Object} quotaData.data - Quota information
 * @param {string} quotaData.signature - HMAC signature
 * @param {number} quotaData.timestamp - Unix timestamp
 * @param {string} quotaData.expiresAt - ISO date string
 * @returns {boolean} True if signature exists and not expired
 */
export const isSignatureValid = (quotaData) => {
  // Check if quota data exists
  if (!quotaData) {
    console.warn('[SignatureValidator] No quota data provided');
    return false;
  }

  // Check if signature exists
  if (!quotaData.signature || typeof quotaData.signature !== 'string') {
    console.warn('[SignatureValidator] Missing or invalid signature');
    return false;
  }

  // Check if timestamp exists
  if (!quotaData.timestamp || typeof quotaData.timestamp !== 'number') {
    console.warn('[SignatureValidator] Missing or invalid timestamp');
    return false;
  }

  // Check if signature is expired (client-side check)
  const now = Math.floor(Date.now() / 1000);
  const age = now - quotaData.timestamp;
  const maxAge = 300; // 5 minutes in seconds

  if (age > maxAge) {
    console.warn(`[SignatureValidator] Signature expired (age: ${age}s, max: ${maxAge}s)`);
    return false;
  }

  // All checks passed
  return true;
};

/**
 * Checks if signature is expired based on timestamp
 * 
 * @param {number} timestamp - Unix timestamp
 * @returns {boolean} True if expired
 */
export const isSignatureExpired = (timestamp) => {
  if (!timestamp) return true;
  
  const now = Math.floor(Date.now() / 1000);
  const age = now - timestamp;
  const maxAge = 300; // 5 minutes
  
  return age > maxAge;
};

/**
 * Gets remaining seconds until signature expires
 * 
 * @param {number} timestamp - Unix timestamp
 * @returns {number} Seconds remaining (0 if expired)
 */
export const getSecondsUntilExpiry = (timestamp) => {
  if (!timestamp) return 0;
  
  const now = Math.floor(Date.now() / 1000);
  const age = now - timestamp;
  const maxAge = 300;
  const remaining = maxAge - age;
  
  return Math.max(0, remaining);
};

/**
 * Formats expiry time for display
 * 
 * @param {number} timestamp - Unix timestamp
 * @returns {string} Human-readable expiry time (e.g., "4m 32s")
 */
export const formatExpiryTime = (timestamp) => {
  const seconds = getSecondsUntilExpiry(timestamp);
  
  if (seconds === 0) return 'Expired';
  
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  
  if (minutes > 0) {
    return `${minutes}m ${remainingSeconds}s`;
  }
  
  return `${remainingSeconds}s`;
};

/**
 * Validates quota data structure
 * 
 * @param {Object} quotaData - Quota response
 * @returns {boolean} True if structure is valid
 */
export const isQuotaDataValid = (quotaData) => {
  if (!quotaData || typeof quotaData !== 'object') {
    return false;
  }

  // Check for required fields
  const hasData = quotaData.data && typeof quotaData.data === 'object';
  const hasTier = hasData && typeof quotaData.data.tier === 'string';
  const hasSignature = typeof quotaData.signature === 'string';
  const hasTimestamp = typeof quotaData.timestamp === 'number';

  return hasData && hasTier && hasSignature && hasTimestamp;
};

/**
 * Extracts user tier from quota data
 * 
 * @param {Object} quotaData - Quota response
 * @returns {string} User tier ('free' or 'premium')
 */
export const getUserTier = (quotaData) => {
  return quotaData?.data?.tier || 'free';
};

/**
 * Checks if user is premium based on quota data
 * 
 * @param {Object} quotaData - Quota response
 * @returns {boolean} True if user is premium
 */
export const isPremiumUser = (quotaData) => {
  return getUserTier(quotaData) === 'premium';
};

/**
 * Comprehensive validation for template access
 * 
 * @param {Object} quotaData - Quota response
 * @param {boolean} templateIsPremium - Whether template requires premium
 * @returns {Object} Validation result with { allowed, reason }
 */
export const validateTemplateAccess = (quotaData, templateIsPremium) => {
  // If template is free, always allow
  if (!templateIsPremium) {
    return { allowed: true, reason: null };
  }

  // Check quota data structure
  if (!isQuotaDataValid(quotaData)) {
    return { 
      allowed: false, 
      reason: 'Invalid quota data. Please refresh the page.' 
    };
  }

  // Check if user is premium
  if (isPremiumUser(quotaData)) {
    return { allowed: true, reason: null };
  }

  // Check signature validity
  if (!isSignatureValid(quotaData)) {
    return { 
      allowed: false, 
      reason: 'Your session has expired. Please refresh the page.' 
    };
  }

  // Free user with valid signature - allow preview but not download
  return { 
    allowed: false, 
    reason: 'This is a premium template. Upgrade to download.' 
  };
};

export default {
  isSignatureValid,
  isSignatureExpired,
  getSecondsUntilExpiry,
  formatExpiryTime,
  isQuotaDataValid,
  getUserTier,
  isPremiumUser,
  validateTemplateAccess,
};
